import { useEffect } from 'react';

import { HeroMikeAndChris } from '../Hero/HeroMikeAndChris';
import { TakeOurQuizCTA } from '../Hero/TakeOurQuizCTA';
import { useAbTestTracking } from '../../hooks/useAbTestTracking';
import { HEADLINE_VARIANTS } from './HeadlineVariants';
import { MedicationStockBanner } from './MedicationStockBanner';
import { REMOTE_DATA_STATUS, RemoteData } from '../../types/remoteData';

import HeroImage from '@public/img/home-page/hero-chris-and-mike.png';

const DEFAULT_VARIANT_INDEX = 10;
const DEFAULT_VARIANT = HEADLINE_VARIANTS[DEFAULT_VARIANT_INDEX];

const getHeadlineVariant = (
  variantNameRemoteData: RemoteData<string, string>,
) => {
  switch (variantNameRemoteData.type) {
    case REMOTE_DATA_STATUS.LOADING:
    case REMOTE_DATA_STATUS.NOT_ASKED:
    case REMOTE_DATA_STATUS.FAILURE:
      return DEFAULT_VARIANT;

    /**
     * If we successfully fetch the A/B test, use the variant assigned to
     * this user.
     */
    case REMOTE_DATA_STATUS.SUCCESS: {
      const selectedVariant = HEADLINE_VARIANTS.find(
        v => v.rank === Number(variantNameRemoteData.data),
      );

      return selectedVariant || DEFAULT_VARIANT;
    }

    default: {
      const _exhaustiveCheck: never = variantNameRemoteData;
      return _exhaustiveCheck;
    }
  }
};

type Props = {
  utmCampaign: string;
  utmMedium: string;
  location: string;
};

export const HomepageHero = ({
  utmMedium,
  utmCampaign,
  location,
}: Props): JSX.Element => {
  const { variantNameRemoteData, registerImpression } =
    useAbTestTracking('718');

  const isFetchingVariant = ['NotAsked', 'Loading'].includes(
    variantNameRemoteData.type,
  );

  /**
   * While we're waiting for the A/B test variant to load, we the default
   * variant with the class name "invisible" to hide the headline.
   * This prevents layout shift when the variant is successfully loaded.
   */
  const headlineClassNames = isFetchingVariant ? 'invisible' : '';

  const { title, description, rank } = getHeadlineVariant(
    variantNameRemoteData,
  );

  const shouldShowSubtitle = rank === DEFAULT_VARIANT_INDEX + 1;

  useEffect(() => {
    registerImpression();
  }, [registerImpression]);

  return (
    <HeroMikeAndChris
      title={
        <div className="md:mt-8">
          <span className={`text-blue ${headlineClassNames}`}>{title}</span>
        </div>
      }
      topContent={
        <MedicationStockBanner
          utmCampaign={utmCampaign}
          utmMedium={utmMedium}
        />
      }
      subtitle={
        shouldShowSubtitle && (
          <span className={`text-blue-800 ${headlineClassNames}`}>
            Mike and Chris, founders of Second Nature
          </span>
        )
      }
      description={
        <span className={`text-blue-800 ${headlineClassNames}`}>
          {description}
        </span>
      }
      image={HeroImage}
      altText="Chris and Mike"
      color="bg-salmon"
      placeholder="blur"
      priority
      ctaChildren={
        !isFetchingVariant && (
          <div className="whitespace-nowrap pb-60 md:pb-0">
            <TakeOurQuizCTA
              utmMedium={utmMedium}
              utmCampaign={utmCampaign}
              location={location}
              utmContent="homepage_top_quiz"
            />
          </div>
        )
      }
    />
  );
};
